import { LLMError } from './types';

export interface SSEEvent {
  event?: string;
  data: string;
}

/** Parses a text/event-stream body into events; handles chunk boundaries and CRLF. */
export async function* readSSE(res: Response): AsyncGenerator<SSEEvent> {
  if (!res.body) return;
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer = (buffer + decoder.decode(value, { stream: true })).replace(/\r\n/g, '\n');
      let boundary: number;
      while ((boundary = buffer.indexOf('\n\n')) >= 0) {
        const event = parseEvent(buffer.slice(0, boundary));
        buffer = buffer.slice(boundary + 2);
        if (event) yield event;
      }
    }
    buffer += decoder.decode();
    const tail = parseEvent(buffer);
    if (tail) yield tail;
  } finally {
    reader.releaseLock();
  }
}

export function parseEvent(block: string): SSEEvent | undefined {
  let event: string | undefined;
  const data: string[] = [];
  for (const line of block.split('\n')) {
    if (!line || line.startsWith(':')) continue;
    const colon = line.indexOf(':');
    const field = colon < 0 ? line : line.slice(0, colon);
    let value = colon < 0 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) value = value.slice(1);
    if (field === 'event') event = value;
    else if (field === 'data') data.push(value);
  }
  return data.length ? { event, data: data.join('\n') } : undefined;
}

/** Drains an SSE response, forwarding each text piece `pick` extracts; `[DONE]` ends the stream. */
export async function collectSSE(res: Response, pick: (data: string, event?: string) => string | undefined, onDelta?: (text: string) => void): Promise<string> {
  let text = '';
  try {
    for await (const ev of readSSE(res)) {
      if (ev.data === '[DONE]') break;
      const piece = pick(ev.data, ev.event);
      if (piece) {
        text += piece;
        onDelta?.(piece);
      }
    }
  } catch (e) {
    if (e instanceof LLMError) throw e;
    if ((e as Error)?.name === 'AbortError') throw new LLMError('aborted', '已手动停止');
    throw new LLMError('network', `流式读取中断：${(e as Error)?.message ?? String(e)}`);
  }
  return text;
}

export function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
